import React, { FC, useEffect, useMemo, useState } from "react";
import { Table } from "react-bootstrap";
import { debounce } from "lodash";
import {
  CustomButton,
  Loader,
  PaginationModel,
  SingleSelect,
  TableData,
  TableHead,
  TableLayout,
  TablePagination,
  TableRow,
  TBody,
  THead,
} from "@mindmemobile/mm-shared-global";
import { FundRaiserService } from "../../shared/services";
import {
  IAPIAgencySummaryModel,
  IAPIFundRaiserSummaryModel,
} from "../../shared/interfaces";
import { FundRaiserStatus, MerchantAccountsGateway } from "../../shared/enums";
import CustomPlaceholders from "../../shared/components/CustomPlaceholders";
import DonationIcon from "../../../../../images/placeholders/heart-muted-grey.svg";
import AdminAuthStore from "../../shared/store/AdminAuthStore";

const PAGE_SIZE = 25;

// Same palette approach as AdminParentAccountsGrid.tsx, kept consistent across the app.
const getStatusStyles = (status: FundRaiserStatus) => {
  switch (status) {
    case FundRaiserStatus.Draft:
      return { backgroundColor: "#F9F9F9", color: "#95A5A6" };
    case FundRaiserStatus.Active:
      return { backgroundColor: "#EEF7EA", color: "#2ECC71" };
    case FundRaiserStatus.Inactive:
      return { backgroundColor: "#FDEDEC", color: "#E74C3C" };
    case FundRaiserStatus.Completed:
      return { backgroundColor: "#ECF0FF", color: "#6A7AB5" };
    default:
      return { backgroundColor: "#EEEEEE", color: "#555555" };
  }
};

// Same palette as AdminParentAccountsGrid.tsx, kept consistent across the app.
const getGatewayStyles = (gateway: keyof typeof MerchantAccountsGateway) => {
  switch (gateway) {
    case MerchantAccountsGateway.Fiserv:
      return { backgroundColor: "#EAF3FC", color: "#3178C6" };
    case MerchantAccountsGateway.NMI:
      return { backgroundColor: "#F3EAFC", color: "#8E44AD" };
    default:
      return { backgroundColor: "#EEEEEE", color: "#555555" };
  }
};

const renderBadge = (styles: { backgroundColor: string; color: string }, label: string) => (
  <CustomButton bgColor={styles.backgroundColor} color={styles.color} fontWeight="600 !important">
    {label}
  </CustomButton>
);

const formatCurrency = (amount: number) =>
  `$${amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

// Explicit UTC components (not toLocaleDateString, which would silently render in the
// viewer's local timezone despite the "(UTC)" column header) - keeps the label accurate.
const formatUtcDate = (date: string | null) => {
  if (!date) return "";
  const parsed = new Date(date);
  if (isNaN(parsed.getTime())) return "";
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${pad(parsed.getUTCMonth() + 1)}/${pad(parsed.getUTCDate())}/${parsed.getUTCFullYear()}`;
};

const AdminFundRaisersGrid: FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [rows, setRows] = useState<IAPIFundRaiserSummaryModel[]>([]);
  const [agencies, setAgencies] = useState<IAPIAgencySummaryModel[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [agencyId, setAgencyId] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [pagination, setPagination] = useState<PaginationModel | null>(null);
  const [error, setError] = useState("");
  // The admin route can mount from the iframe's initial URL before Main_Web2's postMessage
  // carrying the AdminApiKey arrives - wait for it rather than firing requests that 401.
  const [adminApiKey, setAdminApiKeyState] = useState(AdminAuthStore.adminApiKey);

  useEffect(() => AdminAuthStore.subscribe(setAdminApiKeyState), []);

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchValue(value);
        setPageNumber(1);
      }, 400),
    []
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  useEffect(() => {
    if (!adminApiKey) return;

    FundRaiserService.getFundRaiserAgencies().subscribe({
      next: (response) => setAgencies(response.result || []),
      error: (err) => console.log("Error loading agencies", err),
    });
  }, [adminApiKey]);

  useEffect(() => {
    if (!adminApiKey) return;

    setIsLoading(true);
    setError("");

    FundRaiserService.getFundRaisersSummary({
      pageNumber,
      pageSize,
      searchValue,
      agencyId,
    }).subscribe({
      next: (response) => {
        setRows(response.result || []);
        setPagination(PaginationModel.deserialize(response.meta));
        setIsLoading(false);
      },
      error: (err) => {
        console.log("Error loading admin fund raisers grid", err);
        setError("Failed to load fund raisers.");
        setIsLoading(false);
      },
    });
  }, [adminApiKey, pageNumber, pageSize, searchValue, agencyId]);

  const onSearchChange = (value: string) => {
    setSearchInput(value);
    debouncedSearch(value);
  };

  const onClearSearch = () => {
    debouncedSearch.cancel();
    setSearchInput("");
    setSearchValue("");
    setPageNumber(1);
  };

  const onAgencyChange = (value: string) => {
    setAgencyId(value || "");
    setPageNumber(1);
  };

  const agencyOptions = useMemo(
    () => [
      { label: "All Agencies", value: "" },
      ...[...agencies]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((agency) => ({ label: agency.name, value: agency.id })),
    ],
    [agencies]
  );

  return (
    <div style={{ padding: "20px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
          gap: "12px",
        }}
      >
        <h3 style={{ margin: 0 }}>Fund Raisers</h3>
        <div style={{ display: "flex", gap: "12px" }}>
          <SingleSelect
            options={[agencyOptions]}
            nameKeys={["label"]}
            valueKeys={["value"]}
            selectedKey={agencyId}
            emptySelection="All Agencies"
            searchable
            searchPlaceholder="Search agencies"
            onChange={(option) => onAgencyChange(option?.value)}
            width="240px"
            className="default-padding-search-dpdn"
          />
          <div style={{ position: "relative", width: "360px", maxWidth: "100%" }}>
            <input
              type="text"
              placeholder="Search by account, business, sub account or fund raiser name"
              value={searchInput}
              onChange={(e) => onSearchChange(e.target.value)}
              style={{ padding: "8px 32px 8px 12px", width: "100%" }}
            />
            {searchInput && (
              <button
                type="button"
                onClick={onClearSearch}
                aria-label="Clear search"
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  border: "none",
                  background: "transparent",
                  color: "#95A5A6",
                  fontSize: "16px",
                  lineHeight: 1,
                  cursor: "pointer",
                  padding: "4px",
                }}
              >
                &times;
              </button>
            )}
          </div>
        </div>
      </div>
      {error && <div className="text-danger">{error}</div>}
      {isLoading ? (
        <>{new Loader(true).loaderComponent}</>
      ) : rows.length === 0 ? (
        <CustomPlaceholders
          image={DonationIcon}
          label="No Fund Raisers Found"
          description={
            searchValue || agencyId
              ? "Try a different search term or agency filter."
              : "Fund raisers will appear here once accounts create one."
          }
          height="360px"
        />
      ) : (
        <TableLayout>
          <Table className="table-main">
            <THead>
              <TableHead>Account Name</TableHead>
              <TableHead>Sub Account</TableHead>
              <TableHead>Fund Raiser Name</TableHead>
              <TableHead style={{ width: "340px" }}>Merchant Account</TableHead>
              <TableHead style={{ width: "130px", whiteSpace: "normal" }}>Progress</TableHead>
              <TableHead style={{ width: "100px", whiteSpace: "normal" }}>Start Date (UTC)</TableHead>
              <TableHead style={{ width: "100px", whiteSpace: "normal" }}>End Date (UTC)</TableHead>
              <TableHead>Status</TableHead>
            </THead>
            <TBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableData title={row.parentAccountName}>{row.parentAccountName}</TableData>
                  <TableData title={row.subAccountName}>{row.subAccountName}</TableData>
                  <TableData title={row.name}>{row.name}</TableData>
                  <TableData
                    style={{ overflow: "visible", width: "340px" }}
                    title={
                      row.merchantAccountName
                        ? `${row.merchantAccountName}${row.gateway ? ` (${row.gateway})` : ""}`
                        : undefined
                    }
                  >
                    {row.merchantAccountName ? (
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "220px" }}>
                          {row.merchantAccountName}
                        </span>
                        {row.gateway && (
                          <CustomButton
                            bgColor={getGatewayStyles(row.gateway).backgroundColor}
                            color={getGatewayStyles(row.gateway).color}
                            fontWeight="600 !important"
                            style={{ width: "auto", minWidth: "auto", padding: "2px 8px", flexShrink: 0 }}
                          >
                            {row.gateway}
                          </CustomButton>
                        )}
                      </div>
                    ) : (
                      "—"
                    )}
                  </TableData>
                  <TableData
                    style={{ width: "130px" }}
                    title={row.goalAmount != null ? `${formatCurrency(row.totalRaisedAmount)} of ${formatCurrency(row.goalAmount)}` : undefined}
                  >
                    {row.goalAmount != null
                      ? `${formatCurrency(row.totalRaisedAmount)}/${formatCurrency(row.goalAmount)}`
                      : formatCurrency(row.totalRaisedAmount)}
                  </TableData>
                  <TableData style={{ width: "100px" }} title={formatUtcDate(row.startDate)}>{formatUtcDate(row.startDate)}</TableData>
                  <TableData style={{ width: "100px" }} title={formatUtcDate(row.endDate)}>{formatUtcDate(row.endDate)}</TableData>
                  <TableData style={{ overflow: "visible" }} title={row.status}>
                    {renderBadge(getStatusStyles(row.status), row.status)}
                  </TableData>
                </TableRow>
              ))}
            </TBody>
          </Table>
          {pagination && (
            <TablePagination
              pagination={pagination}
              onPageChange={(page: number) => setPageNumber(page)}
              onPageSizeChange={(size: number) => {
                setPageSize(size);
                setPageNumber(1);
              }}
              className="table-pagination"
            />
          )}
        </TableLayout>
      )}
    </div>
  );
};

export default AdminFundRaisersGrid;
